import type { InputHTMLAttributes, ReactElement } from "react";

import { HelpTip } from "@/components/HelpTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface FormFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "className" | "defaultValue" | "id" | "name"
  > {
  readonly label: string;
  /** Form field name, also the input's id. Unique on the page. */
  readonly name: string;
  readonly defaultValue?: string | number | null;
  /** One sentence behind a "?" beside the label, only where the label does not say enough. */
  readonly help?: string;
}

/**
 * One labelled, uncontrolled input for forms read with `FormData`.
 *
 * Uncontrolled on purpose: a form keyed on the record it edits is either empty or
 * pre-filled once, and state per field would buy nothing but re-renders. The other
 * input attributes (`required`, `min`, `pattern`, `list` and so on) pass through.
 */
export function FormField({
  label,
  name,
  defaultValue,
  help,
  type = "text",
  ...input
}: FormFieldProps): ReactElement {
  return (
    <div className="mt-4 grid gap-2.5">
      <div className="flex items-center gap-2.5">
        <Label htmlFor={name}>{label}</Label>
        {help === undefined ? null : <HelpTip topic={label}>{help}</HelpTip>}
      </div>
      <Input
        className="bg-background read-only:text-muted-foreground"
        id={name}
        name={name}
        type={type}
        defaultValue={defaultValue ?? ""}
        {...input}
      />
    </div>
  );
}
